/**
 * Command Autocomplete Component
 * Dropdown for document search when typing @open command
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { searchApi, projectsApi } from '../services/api';
import { logger } from '../utils/logger';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

interface SearchResult {
  id: string;
  documentNumber?: string;
  projectId?: string;
  projectCode?: string;
  title: string;
  type: 'project' | 'company' | 'standard' | 'prior-project';
  score: number;
  snippet?: string;
  canEdit?: boolean;
  metadata?: Record<string, unknown>;
}

interface CommandAutocompleteProps {
  query: string;
  onSelect: (result: SearchResult) => void;
  onClose: () => void;
  position?: { top: number; left: number };
  currentProjectId?: string;
}

// Common industry standards (hardcoded) - using actual documentIds from DynamoDB
const COMMON_STANDARDS: SearchResult[] = [
  {
    id: 'reference-ref_1763834089130_6952',
    documentNumber: 'ref_1763834089130_6952',
    title: 'DO-178C: Software Considerations in Airborne Systems',
    type: 'standard',
    score: 1.0,
    canEdit: false,
    metadata: { standard: '20-140C' }
  },
  {
    id: 'reference-ref_1763853792452_4681',
    documentNumber: 'ref_1763853792452_4681',
    title: 'AC 25.783-1A: Fuselage Doors and Hatches',
    type: 'standard',
    score: 1.0,
    canEdit: false,
    metadata: { standard: '25.783-1A' }
  },
  {
    id: 'reference-ref_1763854224741_8628',
    documentNumber: 'ref_1763854224741_8628',
    title: 'AC 23.1309-1E: Safety Assessment Part 23',
    type: 'standard',
    score: 1.0,
    canEdit: false,
    metadata: { standard: '23.1309-1E' }
  },
];

export default function CommandAutocomplete({
  query,
  onSelect,
  onClose,
  position,
  currentProjectId
}: CommandAutocompleteProps) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [recentDocs, setRecentDocs] = useState<SearchResult[]>([]);
  const [projectDocs, setProjectDocs] = useState<SearchResult[]>([]);

  // Load recent documents from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('aeroRecentDocuments');
      if (saved) {
        const recent = JSON.parse(saved);
        setRecentDocs(recent.slice(0, 5)); // Last 5 documents
      }
    } catch (error) {
      logger.error('Failed to load recent documents:', error);
    }
  }, []);

  // Load current project documents
  useEffect(() => {
    if (!currentProjectId) {
      setProjectDocs([]);
      return;
    }

    const loadProjectDocs = async () => {
      try {
        const docs = await projectsApi.getDocuments(currentProjectId);
        // Convert to SearchResult format and take first 5
        interface ProjectDoc {
          documentNumber: string;
          projectCode?: string;
          projectSpecificTitle?: string;
          documentTitle?: string;
          status?: string;
        }
        const projectResults: SearchResult[] = docs.slice(0, 5).map((doc: ProjectDoc) => ({
          id: doc.documentNumber, // Use documentNumber as canonical ID for deduplication
          documentNumber: doc.documentNumber,
          projectId: currentProjectId,
          projectCode: doc.projectCode,
          title: doc.projectSpecificTitle || doc.documentTitle || doc.documentNumber,
          type: 'project',
          score: 1.0,
          canEdit: true,
          metadata: {
            status: doc.status,
            revision: doc.currentRevision,
          }
        }));
        setProjectDocs(projectResults);
      } catch (error) {
        logger.error('Failed to load project documents:', error);
        setProjectDocs([]);
      }
    };

    loadProjectDocs();
  }, [currentProjectId]);

  // Debounced search
  useEffect(() => {
    // For empty query, show prompt immediately (no search needed)
    if (!query || query.trim().length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }

    // Show loading state immediately for better feedback
    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchApi.searchAllDocuments(query.trim(), 10);
        setResults(response.results || []);
      } catch (error) {
        logger.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Build combined list for keyboard navigation
  const allItems = useMemo(() => {
    return query.trim().length > 0 
      ? results  // Search results only when typing
      : [...recentDocs, ...projectDocs, ...COMMON_STANDARDS]; // Suggestions when empty
  }, [query, results, recentDocs, projectDocs]);

  const handleKeySelect = useCallback((index: number) => {
    if (allItems[index]) {
      onSelect(allItems[index]);
    }
  }, [allItems, onSelect]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allItems.length,
    onSelect: handleKeySelect,
    onClose,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'project':
        return { icon: '📄', label: 'Project Doc', className: 'bg-info-light text-info' };
      case 'company':
        return { icon: '📋', label: 'Company Doc', className: 'bg-secondary text-secondary-foreground' };
      case 'standard':
        return { icon: '📘', label: 'Standard', className: 'bg-success-light text-success' };
      case 'prior-project':
        return { icon: '📦', label: 'Prior Project', className: 'bg-warning-light text-warning' };
      default:
        return { icon: '📄', label: 'Document', className: 'bg-muted text-muted-foreground' };
    }
  };

  // Render a single document item (used for both suggestions and search results)
  const renderDocumentItem = (result: SearchResult, index: number, isSelected: boolean) => {
    const badge = getTypeBadge(result.type);

    return (
      <div
        key={result.id}
        data-index={index}
        onClick={() => onSelect(result)}
        className={`px-4 py-3 cursor-pointer border-b border-border transition-colors ${
          isSelected
            ? 'bg-info-light'
            : 'hover:bg-muted'
        }`}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">{badge.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-foreground truncate">
                {result.title}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${badge.className}`}>
                {badge.label}
              </span>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              {result.documentNumber && (
                <span className="font-mono">{result.documentNumber}</span>
              )}
              {result.projectCode && (
                <>
                  <span>•</span>
                  <span>{result.projectCode}</span>
                </>
              )}
              {result.metadata?.status && (
                <>
                  <span>•</span>
                  <span>{result.metadata.status}</span>
                </>
              )}
            </div>
            {result.snippet && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {result.snippet}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="command-autocomplete"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic positioning
      style={position ? { top: position.top, left: position.left } : undefined}
    >
      <div className="px-4 py-2 bg-info-light border-b border-info">
        <div className="text-xs font-semibold text-info">
          📄 Document Search
        </div>
      </div>

      {loading && (
        <div className="px-4 py-3 text-sm text-muted-foreground">
          🔍 Searching...
        </div>
      )}

      {!loading && query.trim().length === 0 && (
        <div className="max-h-[450px] overflow-y-auto">
          {/* Recently Opened */}
          {recentDocs.length > 0 && (
            <>
              <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                Recently Opened
              </div>
              {recentDocs.map((doc, index) => renderDocumentItem(doc, index, index === selectedIndex))}
            </>
          )}

          {/* Current Project */}
          {projectDocs.length > 0 && (
            <>
              <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
                Current Project
              </div>
              {projectDocs.map((doc, index) => renderDocumentItem(doc, recentDocs.length + index, recentDocs.length + index === selectedIndex))}
            </>
          )}

          {/* Common Standards */}
          <div className="px-4 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase">
            Common Standards
          </div>
          {COMMON_STANDARDS.map((doc, index) => renderDocumentItem(doc, recentDocs.length + projectDocs.length + index, recentDocs.length + projectDocs.length + index === selectedIndex))}

          {/* Help text */}
          {recentDocs.length === 0 && projectDocs.length === 0 && (
            <div className="px-4 py-3 text-center text-sm text-muted-foreground">
              Type to search across all documents
            </div>
          )}
        </div>
      )}

      {!loading && query.trim().length > 0 && results.length === 0 && (
        <div className="px-4 py-8 text-center">
          <div className="text-2xl mb-2">📭</div>
          <div className="text-sm text-muted-foreground">
            No documents found for "{query}"
          </div>
        </div>
      )}

      {!loading && query.trim().length > 0 && results.length > 0 && (
        <div className="max-h-[450px] overflow-y-auto">
          {results.map((result, index) => renderDocumentItem(result, index, index === selectedIndex))}
        </div>
      )}

      <div className="px-4 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>↑↓ Navigate • Enter Select • Esc Close</span>
          {allItems.length > 0 && (
            <span>
              {query.trim().length > 0 
                ? `${results.length} result${results.length !== 1 ? 's' : ''}`
                : `${allItems.length} suggestion${allItems.length !== 1 ? 's' : ''}`
              }
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
